// WSClient — browser-side WebSocket adapter.
//
// Responsibilities (architecture.md §9 / §16, protocol.md §7.2):
// - Open a WS to the configured URL with subprotocol `oekaki-v1`.
// - Encode every outbound `ClientEvent` through the injected codec, after
//   running `clientEventSchema.parse(...)` so we never put a malformed
//   payload on the wire (and any schema bug is caught locally).
// - Decode every inbound frame, validate against `serverEventSchema`, and
//   fan out to subscribers.
// - Surface lifecycle / non-fatal errors via dedicated listener channels.
//
// The class is intentionally event-emitter shaped (no React) so it can be
// instantiated once at module scope and shared via Context.
//
// Auto-reconnect / resume token handling is OUT of Phase 3 scope (Phase 5).

import {
  type ClientEvent,
  type Codec,
  clientEventSchema,
  jsonCodec,
  type ServerEvent,
  SUBPROTOCOL_NAME,
  serverEventSchema,
} from '@oekaki/protocol';

import type { Logger } from '../../application/ports/Logger.js';
import type {
  Transport,
  TransportError,
  TransportErrorListener,
  TransportEventListener,
  TransportStatus,
  TransportStatusListener,
} from '../../application/ports/Transport.js';

/**
 * The minimal WebSocket surface this client uses. We declare it as an
 * interface (not a `typeof WebSocket` import) because tests inject fakes
 * and Vitest's node env has no `WebSocket` global.
 */
export interface WSLike {
  readonly readyState: number;
  send(data: string | ArrayBufferLike | Blob | ArrayBufferView): void;
  close(code?: number, reason?: string): void;
  addEventListener(type: 'open', listener: (ev: Event) => void): void;
  addEventListener(type: 'close', listener: (ev: CloseEvent) => void): void;
  addEventListener(type: 'message', listener: (ev: MessageEvent) => void): void;
  addEventListener(type: 'error', listener: (ev: Event) => void): void;
}

export type WSFactory = (url: string, protocols: string[]) => WSLike;

const defaultFactory: WSFactory = (url, protocols) =>
  new WebSocket(url, protocols) as unknown as WSLike;

export interface WSClientOptions {
  /** Underlying WS factory (browser default; tests inject fakes). */
  wsFactory?: WSFactory;
  /** Wire codec (default JSON). */
  codec?: Codec;
  /** Logger (default no-op). */
  logger?: Logger;
}

const noopLogger: Logger = {
  debug: () => undefined,
  info: () => undefined,
  warn: () => undefined,
  error: () => undefined,
};

/**
 * `WSClient` — concrete `Transport`. Construct once per page session,
 * call `connect(url)` from the join screen, then `send()` / `onEvent()`.
 */
export class WSClient implements Transport {
  private readonly factory: WSFactory;
  private readonly codec: Codec;
  private readonly logger: Logger;

  private socket: WSLike | null = null;
  private _status: TransportStatus = { t: 'idle' };

  private readonly eventListeners = new Set<TransportEventListener>();
  private readonly statusListeners = new Set<TransportStatusListener>();
  private readonly errorListeners = new Set<TransportErrorListener>();

  constructor(options: WSClientOptions = {}) {
    this.factory = options.wsFactory ?? defaultFactory;
    this.codec = options.codec ?? jsonCodec;
    this.logger = options.logger ?? noopLogger;
  }

  get status(): TransportStatus {
    return this._status;
  }

  connect(url: string): void {
    if (this._status.t === 'connecting' || this._status.t === 'open') {
      this.logger.debug('connect ignored: already connecting/open', {
        currentStatus: this._status.t,
      });
      return;
    }
    this.setStatus({ t: 'connecting', url });

    let socket: WSLike;
    try {
      socket = this.factory(url, [SUBPROTOCOL_NAME]);
    } catch (cause) {
      this.logger.error('ws factory threw', {
        err: cause instanceof Error ? cause.message : String(cause),
      });
      this.setStatus({ t: 'closed', code: 1006, reason: 'ws factory failed' });
      return;
    }
    this.socket = socket;

    socket.addEventListener('open', () => {
      this.setStatus({ t: 'open', url });
    });

    socket.addEventListener('message', (ev: MessageEvent) => {
      this.handleIncoming(ev.data as unknown);
    });

    socket.addEventListener('close', (ev: CloseEvent) => {
      this.socket = null;
      this.setStatus({ t: 'closed', code: ev.code, reason: ev.reason ?? '' });
    });

    socket.addEventListener('error', () => {
      // Browsers emit 'error' with no useful payload; log only.
      this.logger.warn('ws error event');
    });
  }

  send(event: ClientEvent): void {
    // Validate locally so a wire-shape bug surfaces in the offending tab,
    // not as a vague server-side `system.error`.
    const parsed = clientEventSchema.safeParse(event);
    if (!parsed.success) {
      this.emitError({
        t: 'send-failed',
        cause: new Error(
          `client event failed local validation: ${parsed.error.issues
            .slice(0, 3)
            .map((i) => i.message)
            .join('; ')}`,
        ),
      });
      return;
    }

    if (this._status.t !== 'open' || this.socket === null) {
      this.emitError({ t: 'send-before-open', eventType: parsed.data.t });
      return;
    }
    try {
      const encoded = this.codec.encode(parsed.data);
      // `WebSocket.send` accepts strings or binary; both pass straight through.
      this.socket.send(encoded as string | ArrayBufferLike);
    } catch (cause) {
      this.emitError({ t: 'send-failed', cause });
    }
  }

  close(code = 1000, reason = ''): void {
    if (this.socket === null) {
      // Idempotent: if we never connected, no-op.
      if (this._status.t !== 'closed') {
        this.setStatus({ t: 'closed', code, reason });
      }
      return;
    }
    if (this._status.t !== 'closing' && this._status.t !== 'closed') {
      this.setStatus({ t: 'closing' });
    }
    try {
      this.socket.close(code, reason);
    } catch (cause) {
      this.logger.warn('ws close threw', {
        err: cause instanceof Error ? cause.message : String(cause),
      });
    }
  }

  onEvent(listener: TransportEventListener): () => void {
    this.eventListeners.add(listener);
    return () => this.eventListeners.delete(listener);
  }

  onStatus(listener: TransportStatusListener): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  onError(listener: TransportErrorListener): () => void {
    this.errorListeners.add(listener);
    return () => this.errorListeners.delete(listener);
  }

  // ─── internal ────────────────────────────────────────────────────────────

  private handleIncoming(raw: unknown): void {
    let decoded: unknown;
    try {
      // Browsers deliver text frames as `string` and binary frames as
      // `ArrayBuffer` / `Blob`. The JSON codec only knows about string &
      // Uint8Array; convert ArrayBuffer at the edge.
      if (typeof raw === 'string') {
        decoded = this.codec.decode(raw);
      } else if (raw instanceof ArrayBuffer) {
        decoded = this.codec.decode(new Uint8Array(raw));
      } else if (raw instanceof Uint8Array) {
        decoded = this.codec.decode(raw);
      } else {
        // Blob or unsupported — surface as decode error rather than crash.
        throw new Error(
          `unsupported WS frame type: ${
            typeof raw === 'object' && raw !== null ? raw.constructor.name : typeof raw
          }`,
        );
      }
    } catch (cause) {
      this.emitError({ t: 'decode-failed', raw, cause });
      return;
    }

    const parsed = serverEventSchema.safeParse(decoded);
    if (!parsed.success) {
      this.emitError({
        t: 'validation-failed',
        raw: decoded,
        issues: parsed.error.issues.slice(0, 5).map((i) => ({
          path: i.path as ReadonlyArray<string | number>,
          message: i.message,
        })),
      });
      return;
    }

    this.emitEvent(parsed.data);
  }

  private emitEvent(ev: ServerEvent): void {
    for (const listener of this.eventListeners) {
      try {
        listener(ev);
      } catch (cause) {
        this.logger.error('event listener threw', {
          eventType: ev.t,
          err: cause instanceof Error ? cause.message : String(cause),
        });
      }
    }
  }

  private emitError(err: TransportError): void {
    for (const listener of this.errorListeners) {
      try {
        listener(err);
      } catch (cause) {
        this.logger.error('error listener threw', {
          err: cause instanceof Error ? cause.message : String(cause),
        });
      }
    }
  }

  private setStatus(next: TransportStatus): void {
    this._status = next;
    // Iterate a snapshot, not the live Set. A status listener may synchronously
    // subscribe *another* listener during dispatch — e.g. the SessionProvider
    // invalid-admin-token fallback registers an `open` handler from inside its
    // `closed` handler (via `tx.connect(...)`). With direct `Set` iteration, a
    // listener added mid-dispatch is still visited by the in-flight loop and so
    // receives the SAME event it was never meant to see (here: the just-added
    // `open` handler would get the current `closed`, self-unsubscribe, and then
    // miss the real `open` → the visitor re-join is never sent, leaving the UI
    // stuck at "入室中..."). Snapshotting gives the standard emitter contract:
    // listeners added during a dispatch do not receive that dispatch's event.
    for (const listener of [...this.statusListeners]) {
      try {
        listener(next);
      } catch (cause) {
        this.logger.error('status listener threw', {
          err: cause instanceof Error ? cause.message : String(cause),
        });
      }
    }
  }
}
